import React, { useState } from 'react';
import { Alert } from '@/src/components/Antd';
import Warning from '@/src/components/Antd/Modal/WarningModal';
import StatusTag from 'src/pages/SalesCannelVersion/SalesChannelDiscounts/Components/StatusTag/index';
import SectionWrapper from '@/src/components/Layouts/Section/SectionWrapper';
import css from './css.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, handleOnFormatStoreDate } from 'src/tools/tools';
import { EPlanStatus, EPlanType, CN_REGION } from '@/src/types/common';
import { Icon } from '@/src/tools/icon';
import { replaceI18nVariables } from '@/src/tools/tools';
import { Button } from 'antd';
import { ExportOutlined } from '@ant-design/icons';

interface CurrentPlanProps {
  planType: EPlanType;
  planStatus: EPlanStatus;
  activeDate: string;
  expireDate: string;
  nextBillingDate: string;
  storeRegion: string;
  prices: any[];
  disabled?: boolean;
  updateLoading?: boolean;
  handleManagePlan: () => void;
  handleCancelEnterprisePlan: () => void;
}

const CurrentPlanSection = ({
  planType,
  planStatus,
  activeDate,
  expireDate,
  nextBillingDate,
  storeRegion,
  disabled,
  updateLoading,
  handleManagePlan,
  handleCancelEnterprisePlan,
}: CurrentPlanProps) => {
  const i18n = useI18n();
  const getI18n = (str) => {
    return i18nGet(i18n, str);
  };
  const [showCancelModal, setShowCancelModal] = useState(false);

  const formattedActiveDate = activeDate
    ? handleOnFormatStoreDate(activeDate)
    : '';
  const formattedExpireDate = expireDate
    ? handleOnFormatStoreDate(expireDate)
    : '';
  const formattedNextBillingDate = nextBillingDate
    ? handleOnFormatStoreDate(nextBillingDate)
    : '';

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleModalCancel = () => {
    setShowCancelModal(false);
  };

  const handleModalConfirm = () => {
    handleCancelEnterprisePlan();
    setShowCancelModal(false);
  };

  const isNextBillingDateShow =
    planType === EPlanType.BASIC &&
    planStatus === EPlanStatus.ACTIVE &&
    nextBillingDate;

  const renderAlert = () => {
    let message = '';
    let description = '';
    if (planStatus === EPlanStatus.PENDING) {
      message = getI18n('storePlanAndBilling.index.planneedsrenewal');
      description = replaceI18nVariables(
        getI18n('storePlanAndBilling.index.renewalFailedDesc'),
        expireDate,
      );
    } else if (
      planStatus === EPlanStatus.EXPIRED ||
      planStatus === EPlanStatus.NONE
    ) {
      message = getI18n('storePlanAndBilling.index.planexpired');
      if (planType === EPlanType.TRIAL) {
        description = getI18n('storePlanAndBilling.index.alertFreeExpiredDesc');
      } else if (planType === EPlanType.BASIC) {
        description = getI18n('storePlanAndBilling.index.planExpiredDesc');
      } else if (
        planType === EPlanType.ENTERPRISE ||
        planType === EPlanType.NONE
      ) {
        message = getI18n('storePlanAndBilling.index.noActivePlan');
        description = getI18n('storePlanAndBilling.index.cNRegionOfflineDesc');
      }
    } else if (
      planStatus === EPlanStatus.CANCELLED &&
      planType === EPlanType.BASIC
    ) {
      message = getI18n('storePlanAndBilling.index.noActivePlan');
      description = getI18n('storePlanAndBilling.index.cNRegionOfflineDesc');
    } else {
      return <></>;
    }

    return (
      <Alert
        className={css.certificateAlert}
        // closable={!isPendingWithNoError}
        message={message}
        showIcon
        type={planStatus === EPlanStatus.PENDING ? 'info' : 'error'}
        icon={
          <Icon
            type={
              planStatus === EPlanStatus.PENDING ? 'iconpending' : 'iconfailed'
            }></Icon>
        }
        description={description}
      />
    );
  };

  const formatPlanType = (type: EPlanType) => {
    const typeMap = {
      [EPlanType.TRIAL]: getI18n('storePlanAndBilling.index.freeTrial'),
      [EPlanType.BASIC]: getI18n('storePlanAndBilling.index.basicPlan'),
      [EPlanType.ENTERPRISE]: getI18n(
        'storePlanAndBilling.index.enterprisePlan',
      ),
    };
    return typeMap[type] || type;
  };

  return (
    <SectionWrapper
      titleClassName={css.titleContainerAll}
      title={getI18n('storePlanAndBilling.index.currentPlan')}
      className={css.storeDetailsInfo}>
      <div className={css.contentContainer}>
        {renderAlert()}
        {storeRegion === CN_REGION && planStatus === EPlanStatus.NONE ? (
          <div className={css.noActivePlan}>
            {getI18n('storePlanAndBilling.index.noActivePlan')}
          </div>
        ) : (
          <div>
            <div className={css.planHeader}>
              <h3 className={css.planName}>{formatPlanType(planType)}</h3>
              <div className={css.statusTagWrapper}>
                <StatusTag status={planStatus} />
              </div>
            </div>

            <div className={css.summaryContent}>
              {formattedActiveDate && (
                <div className={css.dateItem}>
                  <span>• </span>
                  <span className={css.dateText}>
                    {getI18n('storePlanAndBilling.index.planActiveDate')}:{' '}
                    {formattedActiveDate}
                  </span>
                </div>
              )}
              {formattedExpireDate && (
                <div className={css.dateItem}>
                  <span>• </span>
                  <span className={css.dateText}>
                    {getI18n('storePlanAndBilling.index.planExpireDate')}:{' '}
                    {formattedExpireDate}
                  </span>
                </div>
              )}
              {isNextBillingDateShow && (
                <div className={css.dateItem}>
                  <span>• </span>
                  <span className={css.dateText}>
                    {getI18n('storePlanAndBilling.index.nextBillingDate')}:{' '}
                    {formattedNextBillingDate}
                  </span>
                </div>
              )}
            </div>

            <div className={css.actionContainer}>
              {planType === EPlanType.TRIAL &&
                planStatus === EPlanStatus.ACTIVE && (
                  <div className={css.reminderText}>
                    {getI18n('storePlanAndBilling.index.reminderDesc')}
                  </div>
                )}
              {planType === EPlanType.BASIC &&
                (planStatus === EPlanStatus.ACTIVE ||
                  planStatus === EPlanStatus.PENDING) && (
                  <Button
                    className={css.manageButton}
                    onClick={() => handleManagePlan()}
                    disabled={disabled || updateLoading}>
                    <span>
                      {getI18n('storePlanAndBilling.index.manageYourPlan')}
                    </span>
                    <ExportOutlined
                      className={css.externalIcon}
                      rev={undefined}
                    />
                  </Button>
                )}
              {planType === EPlanType.ENTERPRISE &&
                planStatus === EPlanStatus.ACTIVE && (
                  <Button
                    className={css.cancelButton}
                    onClick={handleCancelClick}
                    disabled={disabled}>
                    {getI18n('storePlanAndBilling.index.cancelPlan')}
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>

      {/* Cancel Plan Confirmation Modal */}
      <Warning
        visible={showCancelModal}
        header={getI18n('storePlanAndBilling.index.cancelEnterprisePlan')}
        onOk={handleModalConfirm}
        onCancel={handleModalCancel}
        cancelText={getI18n('general.discard')}
        okText={getI18n('general.confirm')}>
        <div
          style={{
            padding: '8px 8px 16px 8px',
            color: '#3B3F45',
            lineHeight: '1.4',
          }}>
          {getI18n('storePlanAndBilling.index.cancelDesc1')}
          <br />
          {getI18n('storePlanAndBilling.index.cancelDesc2')}
        </div>
      </Warning>
    </SectionWrapper>
  );
};

export default CurrentPlanSection;
